import { Grid, Hidden, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { AutoPxPriceLabel, CrossOut, moneyFormat } from 'design-system';
import React, { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import {
  getTotalByQuotation,
  getValidTotal,
} from 'data-layer-cr/dist/functions/quotations';
import { Quotation } from 'data-layer-cr/dist/models';
import { appliesNewAutoPxDiscount } from 'src/store/quotation/actions/helperMethods';

// models
import CarItem from '../../../../../../models/helperTypes/CarItem';
import CustomerItem from '../../../../../../models/helperTypes/CustomerItem';

// store
import { RootState } from '../../../../../../store/rootReducer';

// Functions
import { canShowQuotationMonthlyFees } from '../../../../../../functions/quotationMethods';
import PricingProfileResponse from '../../../../../../models/responseTypes/PricingProfileResponse';
import { displayFullNameCustomer } from '../../functions/displayContent';
import useDetailsHeaderStyles from './useDetailsHeaderStyles';

const { formatMoney } = moneyFormat;

type PositionType = 'fixed' | 'absolute' | 'sticky' | 'static' | 'relative';

function makeAnualPaymentForDesktop(
  canShowMonthlyFees: boolean,
  inlineWhiteClass: string,
  spacedWhiteText: string,
  grandTotal: number,
  currentProfile: PricingProfileResponse,
): React.ReactNode {
  return (
    <Grid
      data-testid="annual-desk"
      item
      xs={5}
      container
      justifyContent={canShowMonthlyFees ? 'flex-start' : 'center'}
      alignItems="center"
    >
      <Typography variant="h4" className={inlineWhiteClass}>
        Total
      </Typography>
      <Typography variant="h1" className={spacedWhiteText}>
        {formatMoney(grandTotal, false, currentProfile.typeOfMoney)}
      </Typography>
      <Typography variant="caption" className={inlineWhiteClass}>
        / anual
      </Typography>
    </Grid>
  );
}

function makeMonthlyPaymentsForMobile(
  classes: ReturnType<typeof useDetailsHeaderStyles>,
  paymentQuantity: number,
  grandTotal: number,
  currentProfile: PricingProfileResponse,
): React.ReactNode {
  return (
    <div data-testid="monthly" className={classes.priceWrapper}>
      <Typography variant="caption" className={classes.whiteText}>
        <CrossOut
          numberToCrossOut={paymentQuantity}
          maxQuote={paymentQuantity}
        />
        cuotas
      </Typography>
      <br />
      <Typography variant="h2" className={classes.whiteText}>
        {formatMoney(
          grandTotal / paymentQuantity,
          true,
          currentProfile.typeOfMoney,
        )}
      </Typography>
      <Typography variant="caption" className={classes.whiteText}>
        mensual
      </Typography>
    </div>
  );
}

function makeMonthlyPaymentForDesktop(
  inlineWhiteClass: string,
  spacedWhiteText: string,
  grandTotal: number,
  paymentQuantity: number,
  currentProfile: PricingProfileResponse,
): React.ReactNode {
  return (
    <Grid
      item
      xs={5}
      container
      justifyContent="flex-end"
      alignItems="center"
      data-testid="monthly-desk"
    >
      <Typography
        variant="h4"
        className={inlineWhiteClass}
        data-testid="monthy-payment-qty"
      >
        <CrossOut
          numberToCrossOut={paymentQuantity}
          maxQuote={paymentQuantity}
        />
        cuotas
      </Typography>
      <Typography variant="h1" className={spacedWhiteText}>
        {formatMoney(
          (grandTotal / paymentQuantity).toFixed(2),
          false,
          currentProfile.typeOfMoney,
        )}
      </Typography>
      <Typography variant="caption" className={inlineWhiteClass}>
        / mes
      </Typography>
    </Grid>
  );
}

export const DetailsHeader = ({
  quotation,
  currentCustomer,
  visibleContent = true,
  position = 'fixed',
  currentProfile,
  currentCar,
}: Props): JSX.Element => {
  const classes = useDetailsHeaderStyles({ visible: visibleContent });
  const { grandTotal, discountType } = getTotalByQuotation(
    getValidTotal(quotation),
    quotation,
  );
  const isAutoPx = discountType === 'AutoPx';
  const usedTotal = isAutoPx ? grandTotal : getValidTotal(quotation).grandTotal;
  /** Full name will only show when it is quoting, hence, currentCustomer */
  const fullName = displayFullNameCustomer(currentCustomer);
  const appliesForNewAutoPxDiscount = appliesNewAutoPxDiscount(isAutoPx);
  const paymentQuantity = appliesForNewAutoPxDiscount ? 11 : 12;
  const canShowMonthlyFees = canShowQuotationMonthlyFees(quotation);
  const inlineWhiteClass = useMemo(
    () => clsx(classes.forcedInlineText, classes.whiteText),
    [classes],
  );
  const spacedWhiteText = useMemo(
    () => clsx(inlineWhiteClass, classes.spacedText),
    [classes, inlineWhiteClass],
  );

  return (
    <>
      {/** Title Content */}
      <Grid container className={classes.visibleContent} data-testid="content">
        {/* Mobile */}
        <Hidden mdUp>
          <Grid item xs={12} container justifyContent="center">
            <Typography
              data-testid="fullname"
              variant="body2"
              className={classes.whiteText}
            >
              {fullName}
            </Typography>
            {isAutoPx && (
              <Grid item xs={12} container justifyContent="center">
                <AutoPxPriceLabel
                  data-testid="autopx-label"
                  variant="withAutoPx"
                  color="secondary"
                />
              </Grid>
            )}
          </Grid>
          {currentProfile.type === 'colectiva' ? (
            <Grid
              item
              xs={12}
              container
              alignItems="center"
              justifyContent="center"
              data-testid="subtitle-collective-mobile"
            >
              {canShowMonthlyFees &&
                makeMonthlyPaymentsForMobile(
                  classes,
                  paymentQuantity,
                  usedTotal,
                  currentProfile,
                )}
              <div className={classes.priceWrapper}>
                <Typography variant="caption" className={classes.whiteText}>
                  Total
                </Typography>
                <br />
                <Typography
                  variant="h2"
                  className={classes.whiteText}
                  data-testid="total-preview"
                >
                  {formatMoney(usedTotal, true, currentProfile.typeOfMoney)}
                </Typography>
                <Typography variant="caption" className={classes.whiteText}>
                  anual
                </Typography>
              </div>
            </Grid>
          ) : (
            <Grid
              item
              xs={12}
              container
              alignItems="center"
              justifyContent="center"
              data-testid="subtitle-individual-mobile"
              className={classes.spaceMobile}
            >{`${currentCar.model.brand} ${currentCar.model.name} ${currentCar.year}`}</Grid>
          )}
        </Hidden>

        {/* Desktop */}
        <Hidden smDown>
          <Grid item xs={12} container spacing={1}>
            <Grid item xs={12} container justifyContent="center">
              <Typography variant="h3" className={classes.whiteText}>
                {fullName}
              </Typography>
            </Grid>
            {isAutoPx && (
              <Grid item xs={12} container justifyContent="center">
                <AutoPxPriceLabel
                  data-testid="autopx-label"
                  variant="withAutoPx"
                  color="secondary"
                />
              </Grid>
            )}

            {currentProfile.type === 'colectiva' ? (
              <Grid
                item
                xs={12}
                container
                justifyContent="center"
                data-testid="subtitle-collective-desktop"
              >
                {canShowMonthlyFees && (
                  <>
                    {makeMonthlyPaymentForDesktop(
                      inlineWhiteClass,
                      spacedWhiteText,
                      usedTotal,
                      paymentQuantity,
                      currentProfile,
                    )}
                    <Grid item xs={1} container justifyContent="center">
                      <div className={classes.separator} />
                    </Grid>
                  </>
                )}
                {makeAnualPaymentForDesktop(
                  canShowMonthlyFees,
                  inlineWhiteClass,
                  spacedWhiteText,
                  usedTotal,
                  currentProfile,
                )}
              </Grid>
            ) : (
              <Grid
                item
                xs={12}
                container
                alignItems="center"
                justifyContent="center"
                data-testid="subtitle-individual-desktop"
                className={classes.spaceDesktop}
              >{`${currentCar.model.brand} ${currentCar.model.name} ${currentCar.year}`}</Grid>
            )}
          </Grid>
        </Hidden>
      </Grid>

      {position === 'fixed' && <div className={classes.toolbarOffset} />}
    </>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  currentCustomer: CustomerItem;
  currentProfile: PricingProfileResponse;
  currentCar: CarItem;
} => ({
  quotation: state.quotation.currentQuotation,
  currentCustomer: state.customer.currentCustomer,
  currentProfile: state.quotation.currentProfile,
  currentCar: state.car.currentCar,
});

const connector = connect(mapStateToProps, {});
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  hideBackButton?: boolean;
  visibleContent?: boolean;
  position?: PositionType;
}

export default connector(DetailsHeader);
